import { inject, Injectable } from '@angular/core';
import type { Camera } from '@hunting-nature/shared-core/interfaces/camera.interface';
import { createLogger } from '@hunting-nature/shared-core/logging';
import { SubscriptionPlanValues } from '@hunting-nature/shared-core/types/subscription-plan.type';
import { catchError, from, of, switchMap, throwError, type Observable } from 'rxjs';

import { AppConfigLoader } from '../../../config/app-config.loader';
import { db as database } from '../../../databases/app.db';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { CacheService } from '../../../shared/services/cache.service';
import { RestService } from '../../../shared/services/rest-service';
import { ImageProcessingService } from '../../gallery/image-gallery/services/image-processing.service';

@Injectable({
  providedIn: 'root',
})
export class CameraDeletionService {
  // services
  private readonly restService = inject(RestService);
  private readonly cacheService = inject(CacheService);
  private readonly imageProcessingService = inject(ImageProcessingService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly appConfigLoader = inject(AppConfigLoader);
  private readonly log = createLogger('CameraDeletionService');

  /**
   * Deletes an owned camera. A camera assigned to the subscription is removed from it first so the slot is freed;
   * if the deletion fails afterwards, the camera is assigned again so the user does not lose the slot assignment.
   */
  deleteCamera$(camera: Camera): Observable<void> {
    const deleted$ = this.isAssignedToSubscription(camera) ? this.deleteSubscribedCamera$(camera.id) : this.restService.deleteCamera$(camera.id);

    return deleted$.pipe(switchMap(() => from(this.purgeCachedCamera$(camera))));
  }

  /**
   * Removes a camera shared with the user. The backend revokes the share instead of deleting the camera.
   */
  removeSharedCamera$(camera: Camera): Observable<void> {
    return this.restService.deleteCamera$(camera.id).pipe(switchMap(() => from(this.purgeCachedCamera$(camera))));
  }

  private isAssignedToSubscription(camera: Camera): boolean {
    return !!camera.subscriptionPlan && camera.subscriptionPlan !== SubscriptionPlanValues.NONE;
  }

  private deleteSubscribedCamera$(cameraId: string): Observable<void> {
    return this.restService
      .removeSubscriptionCamera$(cameraId)
      .pipe(
        switchMap(() =>
          this.restService.deleteCamera$(cameraId).pipe(catchError((error: unknown) => this.restoreSubscriptionAfterFailedDeletion$(cameraId, error)))
        )
      );
  }

  private restoreSubscriptionAfterFailedDeletion$(cameraId: string, error: unknown): Observable<never> {
    return this.restService.addSubscriptionCamera$(cameraId).pipe(
      catchError((rollbackError: unknown) => {
        this.log.error('Restoring subscription after failed camera deletion failed', { cameraId, error: rollbackError });
        return of(undefined);
      }),
      switchMap(() => throwError(() => error))
    );
  }

  /**
   * Removes the cached API responses and the cached images of the camera. The camera is already gone on the backend,
   * so a cleanup failure is only logged.
   */
  private async purgeCachedCamera$(camera: Camera): Promise<void> {
    try {
      const { apiUrl, remoteControlApiUrl } = this.appConfigLoader.activeConfig().backend;
      const cacheDeletions$ = [this.cacheService.deleteByUrlPrefix$(`${apiUrl}/cameras/${camera.id}`)];
      if (camera.imei) {
        cacheDeletions$.push(this.cacheService.deleteByUrlPrefix$(`${remoteControlApiUrl}/camera-state/${camera.imei}/`));
      }
      await Promise.all(cacheDeletions$);

      const imageIds = await database.cachedImages
        .where('loggedInCustomerId')
        .equals(this.authenticationService.customerId)
        .filter(image => image.cameraId === camera.id)
        .primaryKeys();
      if (imageIds.length > 0) {
        await this.imageProcessingService.delete$(imageIds);
      }
    } catch (error) {
      this.log.error('Purging cached camera failed', { cameraId: camera.id, error });
    }
  }
}
